import { Video, Home, Building2, type LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { ServiceRange } from './types';
import { DELIVERY_MODE_META } from './types';

const ICONS: Record<ServiceRange, LucideIcon> = {
  VIRTUAL: Video,
  IN_HOME: Home,
  IN_OFFICE: Building2,
};

type DeliveryModeTogglesProps = {
  modes: ServiceRange[];
  onToggle: (mode: ServiceRange) => void;
  disabled?: boolean;
};

/**
 * Segmented multi-toggle: enabled modes read as raised white segments inside a
 * warm track, so a row full of defaults stays quiet instead of shouting green.
 */
export function DeliveryModeToggles({
  modes,
  onToggle,
  disabled = false,
}: DeliveryModeTogglesProps) {
  return (
    <div
      role="group"
      aria-label="Delivery modes"
      className="inline-flex items-center gap-0.5 rounded-lg bg-card-subtle p-1"
    >
      {DELIVERY_MODE_META.map(({ value, label }) => {
        const enabled = modes.includes(value);
        const Icon = ICONS[value];
        const isLastEnabled = enabled && modes.length === 1;
        return (
          <button
            key={value}
            type="button"
            aria-label={`${label}: ${enabled ? 'on' : 'off'}`}
            aria-pressed={enabled}
            disabled={disabled || isLastEnabled}
            title={
              isLastEnabled
                ? 'At least one delivery mode is required'
                : `${label}: ${enabled ? 'on' : 'off'}`
            }
            onClick={() => onToggle(value)}
            className={cn(
              'inline-flex h-8 items-center gap-1.5 rounded-md px-2.5 text-sm transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage disabled:cursor-not-allowed',
              enabled
                ? 'bg-white font-medium text-sage shadow-sm'
                : 'text-text-tertiary hover:text-text-primary',
            )}
          >
            <Icon className="h-4 w-4" />
            <span>{label}</span>
          </button>
        );
      })}
    </div>
  );
}
